import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, debounceTime, EMPTY, filter, retry, switchMap, tap } from 'rxjs';
import { ApiUserService } from '@core/api/services/user/api-user.service';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { catchError, map, withLatestFrom } from 'rxjs/operators';
import { userActions } from '@store/actions/user.actions';
import { STORAGE_KEYS, StorageService } from '@core/services/storage/storage.service';
import { TranslateService } from '@ngx-translate/core';
import { languageActions } from '@store/actions/language.actions';
import { languageSelectors } from '@store/selectors/language.selectors';
import { appActions } from '@store/actions/app.actions';
import { AvailableLanguages, LANGUAGES } from '@core/components/header/lang-selection/lang-selection.config';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { enUS as enDateFnsLocale, es as esDateFnsLocale, pt as ptDateFnsLocale } from 'date-fns/locale';
import { userSelectors } from '@store/selectors/user.selectors';

@Injectable()
export class LanguageEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store<StoreState>);
  private readonly _apiUser = inject(ApiUserService);
  private readonly _storage = inject(StorageService);
  private readonly _translateService = inject(TranslateService);
  private readonly _dateAdapter = inject(DateAdapter);
  private _dateLocale = inject(MAT_DATE_LOCALE);
  private _localeId = inject(LOCALE_ID);

  hydrateApp$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(appActions.hydrateApp),
        switchMap(() => this._store.select(languageSelectors.currentLanguage)),
        tap((lang) => {
          this._translateService.use(lang);
          this._updateLocales(lang);
          document.getElementById('Cookiebot')?.setAttribute('data-culture', lang);
        })
      ),
    { dispatch: false }
  );

  userLanguage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(userActions.setUser),
      withLatestFrom(this._store.select(languageSelectors.currentLanguage)),
      map(([{ user }, currentLang]) => {
        if (user.communicationLanguage == null) {
          return languageActions.setCommunicationLanguage({ lang: currentLang });
        }
        const communicationLanguage = LANGUAGES.map((value) => value.lang as string).includes(user.communicationLanguage)
          ? (user.communicationLanguage as AvailableLanguages)
          : currentLang;
        return languageActions.updateWebLanguage({ lang: communicationLanguage });
      })
    )
  );

  updateWebLanguage$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(languageActions.updateWebLanguage),
        filter(({ lang }) => lang !== this._translateService.currentLang),
        tap(({ lang }) => {
          this._storage.setItem(STORAGE_KEYS.LANGUAGE, lang);
          this._translateService.use(lang);
          this._updateLocales(lang);
        }),
        debounceTime(50),
        tap(() => location.reload())
      ),
    { dispatch: false }
  );

  setUserCommunicationLanguage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(languageActions.setCommunicationLanguage),
      concatMap(({ lang }) =>
        this._apiUser.updateCommunicationLanguage(lang).pipe(
          filter((response) => response.data),
          retry(2),
          catchError(() => EMPTY)
        )
      ),
      map(() => userActions.midFetchUser())
    )
  );

  changeLanguage$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(languageActions.changeLanguage),
        tap(({ lang }) => {
          this._updateLocales(lang);
          this._storage.setItem(STORAGE_KEYS.LANGUAGE, lang);
        }),
        withLatestFrom(this._store.select(userSelectors.authUser)),
        concatMap(([{ lang }, user]) =>
          user !== null
            ? this._apiUser.updateCommunicationLanguage(lang).pipe(
                filter((response) => response.data),
                retry(2),
                catchError(() => EMPTY)
              )
            : EMPTY
        ),
        debounceTime(50),
        tap(() => location.reload())
      ),
    {
      dispatch: false,
    }
  );

  private _updateLocales(lang: AvailableLanguages) {
    this._localeId = lang;
    switch (lang) {
      case 'es':
        this._dateLocale = esDateFnsLocale;
        break;
      case 'en':
        this._dateLocale = enDateFnsLocale;
        break;
      case 'pt':
        this._dateLocale = ptDateFnsLocale;
        break;
    }
    this._dateAdapter.setLocale(this._dateLocale);
  }
}
